import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import isHotkey from 'is-hotkey';
import { Editable, withReact, useSlate, Slate } from 'slate-react';
import { Editor, Transforms, createEditor } from 'slate';
import { withHistory } from 'slate-history';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import LooksOneIcon from '@material-ui/icons/LooksOne';
import LooksTwoIcon from '@material-ui/icons/LooksTwo';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import Typography from '@material-ui/core/Typography';
import { cx, css } from 'emotion';

// This component is built using Slate
// https://docs.slatejs.org/
// Comments are left here for posterity. Further optimisation is possible.

// import { Node } from 'slate';
//
// // Define a serializing function that takes a value and returns a string.
// const serialize = value => {
//   return (
//     value
//       // Return the string content of each paragraph in the value's children.
//       .map(n => Node.string(n))
//       // Join them all with line breaks denoting paragraphs.
//       .join('\n')
//   );
// };

// Define a deserializing function that takes a string and returns a value.
const parseText = string => {
  if (string === '' || string === null) {
    return [{type: 'paragraph', children: [{ text: '' }]}];
  }
  // Return a value array of children derived by splitting the string.
  return string.split('\n').map(line => {
    return {
      children: [{ text: line }],
    };
  });
};

const Button = React.forwardRef(
  ({ className, active, reversed, ...props }, ref) => (
    <span
      {...props}
      ref={ref}
      className={cx(
        className,
        css`
          cursor: pointer;
          color: ${reversed
      ? active
        ? 'white'
        : '#535356'
      : active
        ? 'black'
        : '#ccc'};
        `
      )}
    />
  )
);
Button.displayName = 'RTEButton';
Button.propTypes = {
  className: PropTypes.string,
  active: PropTypes.bool.isRequired,
  reversed: PropTypes.string,
};

const Menu = React.forwardRef(
  ({ className, ...props }, ref) => (
    <div
      {...props}
      ref={ref}
      className={cx(
        className,
        css`
          & > * {
            display: inline-block;
          }

          & > * + * {
            margin-left: 15px;
          }
        `
      )}
    />
  )
);
Menu.displayName = 'RTEMenu';
Menu.propTypes = {
  className: PropTypes.string,
};

const Toolbar = React.forwardRef(
  ({ className, ...props }, ref) => (
    <Menu
      {...props}
      ref={ref}
      className={cx(
        className,
        css`
          width: 100%;
          position: relative;
          padding: 14px 10px 10px 10px;
          margin: 0;
          border-bottom: 1px solid #c4c4c4;
          margin-bottom: 20px;
        `
      )}
    />
  )
);
Toolbar.displayName = 'RTEToolbar';
Toolbar.propTypes = {
  className: PropTypes.string,
};

const HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];

const isJson = value => {
  try { JSON.parse(value); }
  catch (e) { return false; }
  return true;
};

const editableStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    padding: '0rem',
    border: '1px solid #c4c4c4',
    borderRadius: '4px',
    '&:hover': {
      border: '1px solid #535356',
    },
    '&:active': {
      border: '1px solid #03489c',
    },
  },
  label: {
    width: '100%',
    marginBottom: '0.5rem',
  },
  toolbar: {
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '0 1rem',
    marginBottom: '1rem',
  },
});

const displayStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    padding: '0rem',
    border: 'none',
  },
  input: {
    width: '100%',
    padding: '0',
    marginBottom: '1rem',
    cursor: 'text',
  },
});

const RichTextField = ({inputValue='', label=null, allowEdit=false, onChange=null}) => {
  const classes = (allowEdit) ? editableStyles() : displayStyles();
  const renderElement = useCallback(props => <Element {...props} />, []);
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  // Migrated DB values are text with some leftover html but will be changed to
  // stringified JSON on save. This function will not be needed if all
  // records were to be updated.
  const parseRte = value => ((!(isJson(value))) || value === null) ? parseText(value) : JSON.parse(value);

  const handleChange = value => allowEdit ? onChange(JSON.stringify(value)) : null;
  return (
    <Slate
      editor={editor}
      value={parseRte(inputValue)}
      onChange={handleChange}
    >
      {label === null ? null : (
        <Typography variant="subtitle2" className={classes.label}>
          {label}
        </Typography>
      )}
      <div className={classes.root}>
        {allowEdit ? (
          <div className={classes.toolbar}>
            <Toolbar>
              <MarkButton format="bold">
                <FormatBoldIcon />
              </MarkButton>
              <MarkButton format="italic">
                <FormatItalicIcon />
              </MarkButton>
              <MarkButton format="underline">
                <FormatUnderlinedIcon />
              </MarkButton>
              <BlockButton format="heading-one">
                <LooksOneIcon />
              </BlockButton>
              <BlockButton format="heading-two">
                <LooksTwoIcon />
              </BlockButton>
              <BlockButton format="block-quote">
                <FormatQuoteIcon />
              </BlockButton>
              <BlockButton format="numbered-list">
                <FormatListNumberedIcon />
              </BlockButton>
              <BlockButton format="bulleted-list">
                <FormatListBulletedIcon />
              </BlockButton>
            </Toolbar>
          </div>
        ) : null}
        <div className={classes.input}>
          {allowEdit ? (
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              placeholder="This field is rich-text enabled...!"
              spellCheck
              onKeyDown={event => {
                for (const hotkey in HOTKEYS) {
                  if (isHotkey(hotkey, event)) {
                    event.preventDefault();
                    const mark = HOTKEYS[hotkey];
                    toggleMark(editor, mark);
                  }
                }
              }}
            />
          ) : (
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              readOnly
            />
          )}
        </div>
      </div>
    </Slate>
  );
};
RichTextField.propTypes = {
  inputValue: PropTypes.string,
  label: PropTypes.string,
  allowEdit: PropTypes.bool,
  onChange: PropTypes.func,
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);
  Transforms.unwrapNodes(editor, {
    match: n => LIST_TYPES.includes(n.type),
    split: true,
  });
  Transforms.setNodes(editor, {
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  });
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};
toggleBlock.propTypes = {
  editor: PropTypes.object.isRequired,
  format: PropTypes.string.isRequired,
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};
toggleMark.propTypes = {
  editor: PropTypes.object.isRequired,
  format: PropTypes.string.isRequired,
};

const isBlockActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: n => n.type === format,
  });
  return !!match;
};
isBlockActive.propTypes = {
  editor: PropTypes.object.isRequired,
  format: PropTypes.string.isRequired,
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};
isMarkActive.propTypes = {
  editor: PropTypes.object.isRequired,
  format: PropTypes.string.isRequired,
};

const Element = ({ attributes, children, element }) => {
  switch (element.type) {
  case 'block-quote':
    return <blockquote {...attributes}>{children}</blockquote>;
  case 'bulleted-list':
    return <ul {...attributes}>{children}</ul>;
  case 'heading-one':
    return <h1 {...attributes}>{children}</h1>;
  case 'heading-two':
    return <h2 {...attributes}>{children}</h2>;
  case 'list-item':
    return <li {...attributes}>{children}</li>;
  case 'numbered-list':
    return <ol {...attributes}>{children}</ol>;
  default:
    return <p {...attributes}>{children}</p>;
  }
};
Element.propTypes = {
  attributes: PropTypes.object.isRequired,
  children: PropTypes.object.isRequired,
  element: PropTypes.object.isRequired,
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.code) {
    children = <code>{children}</code>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  return <span {...attributes}>{children}</span>;
};
Leaf.propTypes = {
  attributes: PropTypes.object.isRequired,
  children: PropTypes.object.isRequired,
  leaf: PropTypes.object.isRequired,
};

const BlockButton = ({ format, children }) => {
  const editor = useSlate();
  return (
    <Button
      active={isBlockActive(editor, format)}
      onMouseDown={event => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      {children}
    </Button>
  );
};
BlockButton.propTypes = {
  format: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
};

const MarkButton = ({ format, children }) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={event => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      {children}
    </Button>
  );
};
MarkButton.propTypes = {
  format: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
};

// const initialValue = [
//   {
//     type: 'paragraph',
//     children: [
//       { text: 'This is editable ' },
//       { text: 'rich', bold: true },
//       { text: ' text, ' },
//     ],
//   },
//   {
//     type: 'paragraph',
//     children: [
//       {
//         text:
//           'Since its rich text, you can do things like turn a selection of text ',
//       },
//       { text: 'bold', bold: true },
//       {
//         text:
//           ', or add a semantically rendered block quote in the middle of the page, like this:',
//       },
//     ],
//   },
//   {
//     type: 'block-quote',
//     children: [{ text: 'A wise quote.' }],
//   },
//   {
//     type: 'paragraph',
//     children: [{ text: 'Try it out for yourself!' }],
//   },
// ];

export default RichTextField;
